import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useHistory } from 'react-router-dom'
import { useAuthContext } from '../../context/auth.context'
import { useApiListAuthOrganization } from '../../api-new/state/auth'
import { contactLine, currentClaims, deletionDay, isRestrictedSession } from './organization-deletion'
import { useDialogFocus } from './dialog-focus'
import { ErrorReference } from '../error/error-reference'
import '../sso-workspace/sso-workspace.scss'

// The sign-in notice (scheduled-org member experience, D5): for each of the
// person's organizations scheduled for deletion whose notice they have not
// acknowledged, one notice at a time, until they acknowledge it. Either
// button acknowledges it (plan Ruling R2); the ack is server-side, so it
// holds on every device. Owners who can restore here keep the banner and
// restore page instead. Never in a restricted or an impersonated session
// (support must not dismiss it; auth refuses that ack anyway). Owners by
// full name, never an address (D2). Modal: no Escape or backdrop dismissal
// -- only an acknowledgement closes it.
export const ScheduledNotice = () => {
  const authContext = useAuthContext()
  const history = useHistory()
  const state = authContext.state || {}
  const claims = currentClaims(state)
  const active = !!state.refreshToken && !isRestrictedSession(claims) && !claims.impersonationEndsAt

  const api = useApiListAuthOrganization()
  const apiRef = useRef(api)
  apiRef.current = api

  const [queue, setQueue] = useState([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [cause, setCause] = useState(null)

  useEffect(() => {
    if (!active) return undefined
    let live = true
    Promise.resolve()
      .then(() => apiRef.current.get(`${state.refreshToken}/scheduled`))
      .then(rows => live && setQueue((Array.isArray(rows) ? rows : []).filter(r => r && r.noticeAcked === false && r.canRestore !== true)))
      .catch(() => {})
    return () => { live = false }
  }, [active, state.refreshToken])

  const current = active ? queue[0] : null
  const shown = !!current

  const dialogRef = useRef(null)
  const gotItRef = useRef(null)
  useDialogFocus(shown, dialogRef, gotItRef)

  // The next notice in the queue, or the same one after a failed save: focus
  // back on Got it (the buttons were disabled while saving).
  const currentID = current ? current.organizationID : null
  const [focusTick, setFocusTick] = useState(0)
  useEffect(() => {
    if (currentID != null && gotItRef.current) gotItRef.current.focus()
  }, [currentID, focusTick])

  if (!current) return null

  const acknowledge = async then => {
    setBusy(true)
    setError(null)
    setCause(null)
    try {
      await apiRef.current.put(`${state.refreshToken}/scheduled/${current.organizationID}/ack`, {})
      setQueue(q => q.slice(1))
      if (then) then()
    } catch (err) {
      setError("We couldn't save that. Try again.")
      setCause(err)
      setFocusTick(t => t + 1)
    } finally {
      setBusy(false)
    }
  }

  const here = claims.organizationName || 'another organization'
  const owner = current.role === 'owner'

  return createPortal(
    <div className="sw-drawer justify-content-center align-items-center">
      <div className="sw-dialog" ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="sched-notice-title" aria-describedby="sched-notice-body">
        <h2 className="h5" id="sched-notice-title">{`${current.name} is scheduled for deletion`}</h2>
        <div id="sched-notice-body">
          <p className="sw-hint">{`It will be permanently deleted on ${deletionDay(current.purgeAfter)}. You've been signed in to ${here} instead.`}</p>
          <p className="sw-hint">{owner ? 'You can restore it from the admin panel.' : `Only an owner of ${current.name} can restore it.`}</p>
          {!owner && <p className="sw-hint">{contactLine(current.ownerNames)}</p>}
        </div>
        {error && <p className="text-danger small" role="alert">{error}</p>}
        {error && <ErrorReference error={cause} />}
        <div className="sw-dialog-actions">
          <button type="button" className="btn sw-btn" disabled={busy} onClick={() => acknowledge(() => history.push('/auth/account'))}>Switch organization</button>
          <button type="button" className="btn sw-btn-primary" ref={gotItRef} disabled={busy} onClick={() => acknowledge()}>Got it</button>
        </div>
      </div>
    </div>,
    document.body
  )
}
